import { useCallback, useEffect, useState } from 'react';
import { dealerErrorMessage, dealersApi } from '../../services/dealersApi';
import { CREDIT_REQUEST_STATUSES, CreditRequest, CreditRequestKind, CreditRequestStatus } from '../../types/dealers';
import CursorPagination from '../CursorPagination';
import DealerStatusChip from './DealerStatusChip';
import MarkPaidDialog from './MarkPaidDialog';
import RevokeDialog from './RevokeDialog';
import { aed, formatDate, humanize, pct } from './dealerFormat';

const PAGE_SIZE = 20;

interface RequestsTableProps {
  dealerId: string;
  token: string;
  notify: (message: string, type?: 'success' | 'error') => void;
  onFeatureOff: () => void;
  /** After a mark-paid or revoke: credits, lots and documents changed too. */
  onChanged?: () => void;
}

/**
 * Spec 085 Flow E — credit requests and pack orders of one dealer. Mark paid on AWAITING_PAYMENT
 * (the dealer accepted the proforma); Revoke on anything not yet paid. Paid rows show the invoice.
 */
export default function RequestsTable({ dealerId, token, notify, onFeatureOff, onChanged }: RequestsTableProps) {
  const [status, setStatus] = useState<CreditRequestStatus | ''>('');
  const [kind, setKind] = useState<CreditRequestKind | ''>('');
  const [rows, setRows] = useState<CreditRequest[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState<CreditRequest | null>(null);
  const [revoking, setRevoking] = useState<CreditRequest | null>(null);

  const fetchPage = useCallback(async (after: string | null) => {
    if (after) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const page = await dealersApi.requests(dealerId, {
        status: status || undefined, kind: kind || undefined, cursor: after ?? undefined, limit: PAGE_SIZE,
      }, token);
      setRows((prev) => (after ? [...prev, ...page.content] : page.content));
      setCursor(page.nextCursor ?? null);
      setHasMore(page.hasMore);
    } catch (err) {
      setError(dealerErrorMessage(err, 'Failed to load requests'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [dealerId, token, status, kind]);

  useEffect(() => { fetchPage(null); }, [fetchPage]);

  const changed = (message: string, type: 'success' | 'error' = 'success') => {
    setPaying(null);
    setRevoking(null);
    notify(message, type);
    fetchPage(null);
    onChanged?.();
  };

  return (
    <div data-testid="dealer-requests">
      <div className="admin-toolbar dealer-toolbar">
        <select className="admin-select" aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value as CreditRequestStatus | '')}>
          <option value="">All statuses</option>
          {CREDIT_REQUEST_STATUSES.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
        </select>
        <select className="admin-select" aria-label="Filter by type" value={kind} onChange={(e) => setKind(e.target.value as CreditRequestKind | '')}>
          <option value="">Credit requests and pack orders</option>
          <option value="PER_BUYER">Per-buyer credit</option>
          <option value="PACK">Pack orders</option>
        </select>
      </div>

      {loading ? (
        <div className="admin-loading"><div className="admin-loading-spinner" /></div>
      ) : error ? (
        <div>
          <div className="admin-info-banner danger" role="alert">{error}</div>
          <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => fetchPage(null)}>Retry</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="admin-empty-state"><p>{status || kind ? 'No requests match these filters.' : 'No credit requests yet.'}</p></div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table dealer-table">
            <thead>
              <tr>
                <th>Proforma</th><th>Type</th><th className="num">Face value</th><th className="num">Discount</th>
                <th className="num">Amount due</th><th>Status</th><th>Created</th><th>Paid / revoked</th><th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const open = r.status === 'PROFORMA_ISSUED' || r.status === 'AWAITING_PAYMENT';
                return (
                  <tr key={r.id} data-testid={`request-${r.id}`}>
                    <td>
                      {r.proformaUrl
                        ? <a className="admin-link" href={r.proformaUrl} target="_blank" rel="noreferrer">{r.number ?? 'Proforma'}</a>
                        : r.number ?? '—'}
                    </td>
                    <td>{r.kind === 'PACK' ? 'Pack order' : 'Per-buyer'}</td>
                    <td className="num">{aed(r.faceValueAed)}</td>
                    <td className="num">{pct(r.discountPct)}</td>
                    <td className="num">{aed(r.totalDueAed)}</td>
                    <td><DealerStatusChip status={r.status} testId="request-status" /></td>
                    <td>{formatDate(r.createdAt)}</td>
                    <td className="dealer-cell-note">
                      {r.status === 'PAID' && <>{r.invoiceNumber ?? '—'} · {r.bankReference ?? ''} · {formatDate(r.valueDate)}</>}
                      {r.status === 'REVOKED' && <>{formatDate(r.revokedAt)}{r.revokeReason ? ` · ${r.revokeReason}` : ''}</>}
                    </td>
                    <td className="dealer-actions">
                      {r.status === 'AWAITING_PAYMENT' && (
                        <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setPaying(r)}>Mark paid</button>
                      )}
                      {open && (
                        <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setRevoking(r)}>Revoke</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!loading && !error && <CursorPagination hasMore={hasMore} loading={loadingMore} onLoadMore={() => fetchPage(cursor)} />}

      {paying && (
        <MarkPaidDialog
          dealerId={dealerId}
          request={paying}
          token={token}
          onClose={() => setPaying(null)}
          onPaid={(res) => changed(`${res.request.number ?? 'Request'} marked paid${res.invoiceNumber ? ` · invoice ${res.invoiceNumber}` : ''}`)}
          onStale={(message) => changed(message, 'error')}
        />
      )}
      {revoking && (
        <RevokeDialog
          dealerId={dealerId}
          request={revoking}
          token={token}
          onClose={() => setRevoking(null)}
          onRevoked={(saved) => changed(`${saved.number ?? 'Request'} revoked`)}
          onStale={(message) => changed(message, 'error')}
          onFeatureOff={onFeatureOff}
        />
      )}
    </div>
  );
}
